import { Link } from "react-router-dom";
import { Zap, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { usePageMeta } from "../lib/seo";
import { Navbar } from "../components/Navbar";

const SECTIONS = [
  {
    img: "/help/wizard.png",
    title: "1. Plan je trip in 7 stappen",
    text: "Klik op 'Nieuwe trip' en doorloop de wizard: kies je data, vertrekuur en batterijniveau, vul vertrek- en eindbestemming in (met tussenstops en plaatsen die je wil vermijden), kies je routevoorkeur, gezelschap, verblijfstype en je elektrische wagen. VoltRoute berekent daarna automatisch je dagetappes, laadstops, overnachtingen en paklijst.",
  },
  {
    img: "/help/planning.png",
    title: "2. Je dag-per-dag planning met laadstops",
    text: "Elke rijdag toont afstand, rijtijd, vertrek- en aankomsttijd, verwachte batterij bij aankomst en het weer. Laadstops langs de route staan erbij met vermogen, laadtijd en kosten — lader bezet? Kies met één klik een alternatief. Pas je vertrekuur per dag aan en download de GPX per dag of voor de hele trip.",
  },
  {
    img: "/help/assistant.png",
    title: "3. Vraag de AI-assistent om wijzigingen",
    text: "De groene assistent-knop rechtsonder beantwoordt vragen ('waar lunchen bij de eerste laadstop?') én past je planning aan: vraag 'rij via Reims en vermijd Parijs' en je krijgt een voorstel-kaartje. Eén klik op 'Pas toe' en je route wordt herberekend — je verslagen en foto's blijven staan.",
  },
  {
    img: "/help/live.png",
    title: "4. Live-modus onderweg",
    text: "Start de Live-modus tijdens het rijden: je ziet je GPS-positie op de route, de volgende laadstop en je overnachting van vanavond. Vul je echte batterijpercentage in en de resterende laadstops worden herrekend. Spreek onderweg je reisverslag gewoon in — AI zet het om in tekst.",
  },
  {
    img: "/help/verslag.png",
    title: "5. Reisverslag & familie laten meekijken",
    text: "In de Verslag-tab groeit je reisdagboek per dag met foto's, video's en (ingesproken) notities. Nodig familie en vrienden uit via e-mail: zij volgen alles live via een persoonlijke link, reageren per dag en tikken een ❤️ op je foto's — zonder account. Jij ziet hun reacties met een teller op je dashboard.",
  },
  {
    img: "/help/budget.png",
    title: "6. Budget, paklijst en delen",
    text: "Volg geschatte én werkelijke kosten (laden, overnachten, eten, extra's) in de Budget-tab, vink je slimme paklijst af en deel je hele trip publiek of genereer een AI-reisverhaal als aandenken. Fouten in je planning? Klik 'Aanpassen' en corrigeer alles — de wizard opent vooringevuld.",
  },
];

export default function HelpPage() {
  usePageMeta("Hoe werkt VoltRoute? — EV-roadtrip plannen in 6 stappen", "Van wizard tot live modus: zo plant VoltRoute jouw elektrische roadtrip met laadstops, overnachtingen, budget en reisdagboek.");
  const { user } = useAuth() || {};
  return (
    <div className="min-h-screen bg-background" data-testid="help-page">
      {user ? <Navbar /> : (
        <header className="sticky top-0 z-40 glass-panel border-b border-border/60">
          <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-full bg-primary flex items-center justify-center"><Zap className="w-5 h-5 text-primary-foreground" /></span>
              <span className="font-heading font-extrabold text-lg tracking-tight">VoltRoute</span>
            </Link>
            <Link to="/auth" className="text-sm font-semibold text-primary hover:underline">Inloggen</Link>
          </div>
        </header>
      )}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-accent mb-2">Handleiding</p>
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mb-2">Hoe werkt VoltRoute?</h1>
        <p className="text-sm text-muted-foreground mb-10">In zes stappen van idee tot elektrische roadtrip — met echte schermen uit de app.</p>

        <div className="space-y-12">
          {SECTIONS.map((s, i) => (
            <section key={s.title} data-testid={`help-section-${i + 1}`} className={`flex flex-col ${i % 2 ? "md:flex-row-reverse" : "md:flex-row"} gap-6 items-center`}>
              <div className="md:w-3/5 w-full">
                <img src={s.img} alt={s.title} loading="lazy"
                  className="rounded-2xl border border-border shadow-[0_12px_40px_rgba(31,38,59,0.12)] w-full" />
              </div>
              <div className="md:w-2/5 w-full">
                <h2 className="font-heading font-bold text-lg mb-2">{s.title}</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.text}</p>
              </div>
            </section>
          ))}
        </div>

        <div className="mt-14 text-center">
          <Link to={user ? "/nieuwe-trip" : "/auth"} data-testid="help-cta-button"
            className="inline-flex items-center gap-2 rounded-full bg-primary hover:bg-[#1B4332] text-primary-foreground px-8 py-4 font-semibold transition-[background-color] duration-200">
            Plan je eerste trip <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </main>
    </div>
  );
}
